import type { DashboardMetricsParams } from "@/domain/dashboard";
import type { TemplateFilters } from "@/domain/template";

export const queryKeys = {
	classifications: {
		all: (orgId: string, beneficiaryUuid: string) =>
			["classifications", orgId, beneficiaryUuid] as const,
		list: (
			orgId: string,
			beneficiaryUuid: string,
			params?: Record<string, unknown>,
		) => ["classifications", orgId, beneficiaryUuid, "list", params] as const,
		detail: (orgId: string, beneficiaryUuid: string, uuid: string) =>
			["classifications", orgId, beneficiaryUuid, "detail", uuid] as const,
		file: (orgId: string, beneficiaryUuid: string, uuid: string) =>
			["classifications", orgId, beneficiaryUuid, "file", uuid] as const,
		summary: (
			orgId: string,
			beneficiaryUuid: string,
			fiscalYear?: number,
			status?: string,
			documentType?: string,
			eligible?: boolean,
			expenseType?: string,
		) =>
			[
				"classifications",
				orgId,
				beneficiaryUuid,
				"summary",
				fiscalYear,
				status,
				documentType,
				eligible,
				expenseType,
			] as const,
		suppliersAnalytics: (
			orgId: string,
			beneficiaryUuid: string,
			fiscalYear?: number,
			expenseType?: string,
		) =>
			[
				...queryKeys.classifications.all(orgId, beneficiaryUuid),
				"suppliers-analytics",
				{ fiscalYear, expenseType },
			] as const,
		taxBreakdown: (
			orgId: string,
			beneficiaryUuid: string,
			fiscalYear?: number,
			expenseType?: string,
		) =>
			[
				...queryKeys.classifications.all(orgId, beneficiaryUuid),
				"tax-breakdown",
				{ fiscalYear, expenseType },
			] as const,
		workforce: (orgId: string, beneficiaryUuid: string, fiscalYear?: number) =>
			[
				...queryKeys.classifications.all(orgId, beneficiaryUuid),
				"workforce",
				{ fiscalYear },
			] as const,
		counts: (
			orgId: string,
			beneficiaryUuid: string,
			params?: Record<string, unknown>,
		) =>
			[
				...queryKeys.classifications.all(orgId, beneficiaryUuid),
				"counts",
				params,
			] as const,
	},
	admin: {
		all: () => ["admin"] as const,
		dashboard: () => ["admin", "dashboard"] as const,
		organizations: (params?: Record<string, unknown>) =>
			["admin", "organizations", params] as const,
		organizationDetail: (uuid: string) =>
			["admin", "organizations", "detail", uuid] as const,
		organizationMembers: (uuid: string) =>
			["admin", "organizations", "members", uuid] as const,
		organizationInvitations: (uuid: string) =>
			["admin", "organizations", "invitations", uuid] as const,
		users: (params?: Record<string, unknown>) =>
			["admin", "users", params] as const,
		userDetail: (uuid: string) => ["admin", "users", "detail", uuid] as const,
		userMemberships: (uuid: string) =>
			["admin", "users", "memberships", uuid] as const,
	},
	investments: {
		all: (orgId: string) => ["investments", orgId] as const,
		list: (
			orgId: string,
			filters?: Record<string, unknown>,
			pagination?: Record<string, unknown>,
			viewMode?: string,
		) => ["investments", orgId, "list", filters, pagination, viewMode] as const,
		detail: (orgId: string, uuid: string) =>
			["investments", orgId, "detail", uuid] as const,
		documents: (orgId: string, uuid: string) =>
			["investments", orgId, "documents", uuid] as const,
	},
	dashboard: {
		all: (orgId: string) => ["dashboard", orgId] as const,
		metrics: (orgId: string, filters?: DashboardMetricsParams) =>
			["dashboard", orgId, "metrics", filters] as const,
	},
	pipeline: {
		all: (orgId: string) => ["pipeline", orgId] as const,
		query: (
			orgId: string,
			params?: {
				beneficiary_uuid?: string;
				fiscal_year?: number;
				fiscal_quota_uuid?: string;
			},
		) => ["pipeline", orgId, "query", params] as const,
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["pipeline", orgId, "metrics", params] as const,
	},
	templates: {
		all: (orgId: string) => ["templates", orgId] as const,
		list: (orgId: string, filters?: TemplateFilters) =>
			["templates", orgId, "list", filters] as const,
		detail: (orgId: string, uuid: string) =>
			["templates", orgId, "detail", uuid] as const,
	},
	placeholders: {
		all: (orgId: string) => ["placeholders", orgId] as const,
		catalog: (orgId: string) => ["placeholders", orgId, "catalog"] as const,
	},
	proposals: {
		all: () => ["proposals"] as const,
		list: (params?: { limit?: number; offset?: number }) =>
			["proposals", "list", params] as const,
		detail: (uuid: string, token: string) =>
			["proposals", "detail", uuid, token] as const,
	},
	hub: {
		all: ["hub"] as const,
		profile: () => ["hub", "profile"] as const,
		dashboard: () => ["hub", "dashboard"] as const,
		pendingActions: (params?: Record<string, unknown>) =>
			["hub", "pendingActions", params] as const,
		investments: (params: Record<string, unknown>) =>
			["hub", "investments", params] as const,
		investmentDetail: (uuid: string) =>
			["hub", "investmentDetail", uuid] as const,
		investmentDocuments: (investmentUuid: string) =>
			["hub", "investmentDocuments", investmentUuid] as const,
	},
	contracts: {
		all: (orgId: string) => ["contracts", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["contracts", orgId, "list", params] as const,
		metrics: (orgId: string) => ["contracts", orgId, "metrics"] as const,
	},
	notifications: {
		all: (orgId: string) => ["notifications", orgId] as const,
		lists: (orgId: string) => ["notifications", orgId, "list"] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["notifications", orgId, "list", params] as const,
		stats: (orgId: string) => ["notifications", orgId, "stats"] as const,
	},
	beneficiaries: {
		all: (orgId: string) => ["beneficiaries", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["beneficiaries", orgId, "list", params] as const,
		detail: (orgId: string, id: string) =>
			["beneficiaries", orgId, "detail", id] as const,
		documents: (
			orgId: string,
			uuid: string,
			params?: Record<string, unknown>,
		) =>
			params !== undefined
				? (["beneficiaries", orgId, "documents", uuid, params] as const)
				: (["beneficiaries", orgId, "documents", uuid] as const),
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["beneficiaries", orgId, "metrics", params] as const,
		fiscalQuotasByBeneficiary: (orgId: string, beneficiaryUuid: string) =>
			["beneficiaries", orgId, "fiscal-quotas", beneficiaryUuid] as const,
		fiscalYearSummary: (orgId: string, beneficiaryUuid: string, year: number) =>
			[
				"beneficiaries",
				orgId,
				"fiscal-year-summary",
				beneficiaryUuid,
				year,
			] as const,
	},
	investorDropdown: {
		all: (orgId: string) => ["investorDropdown", orgId] as const,
		search: (orgId: string, search: string) =>
			["investorDropdown", orgId, search] as const,
	},
	beneficiaryDropdown: {
		all: (orgId: string) => ["beneficiaryDropdown", orgId] as const,
		search: (orgId: string, search: string) =>
			["beneficiaryDropdown", orgId, search] as const,
	},
	templateDropdown: {
		all: (orgId: string) => ["templateDropdown", orgId] as const,
		search: (
			orgId: string,
			search: string,
			filters: Record<string, string | undefined>,
		) => ["templateDropdown", orgId, search, filters] as const,
	},
	investors: {
		all: (orgId: string) => ["investors", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "list", params] as const,
		detail: (orgId: string, uuid: string, type: string) =>
			["investors", orgId, "detail", uuid, type] as const,
		investments: (
			orgId: string,
			uuid: string,
			filters?: Record<string, unknown>,
		) => ["investors", orgId, "investments", uuid, filters] as const,
		investmentMetrics: (orgId: string, uuid: string) =>
			["investors", orgId, "investmentMetrics", uuid] as const,
		documents: (orgId: string, uuid: string) =>
			["investors", orgId, "documents", uuid] as const,
		metrics: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "metrics", params] as const,
		dashboardMetrics: (orgId: string, params?: Record<string, unknown>) =>
			["investors", orgId, "dashboardMetrics", params] as const,
	},
	projects: {
		all: (orgId: string) => ["projects", orgId] as const,
		list: (orgId: string, params?: Record<string, unknown>) =>
			["projects", orgId, "list", params] as const,
		detail: (projectId: string) => ["projects", "detail", projectId] as const,
		query: (queryId: string, params?: Record<string, unknown>) =>
			["projects", "query", queryId, params] as const,
		beneficiaryProjects: (orgId: string, beneficiaryUuid: string) =>
			["projects", orgId, "beneficiaryProjects", beneficiaryUuid] as const,
		beneficiaryProjectsList: (
			orgId: string,
			beneficiaryUuid: string,
			params?: Record<string, unknown>,
		) =>
			params !== undefined
				? ([
						"projects",
						orgId,
						"beneficiaryProjects",
						beneficiaryUuid,
						"list",
						params,
					] as const)
				: ([
						"projects",
						orgId,
						"beneficiaryProjects",
						beneficiaryUuid,
						"list",
					] as const),
		poster: (projectId: string) =>
			["projects", "detail", projectId, "poster"] as const,
	},
	userProfile: {
		all: () => ["userProfile"] as const,
		current: () => ["userProfile", "current"] as const,
	},
	modules: {
		all: (orgId: string) => ["modules", orgId] as const,
		entitlements: (orgId: string) =>
			["modules", orgId, "entitlements"] as const,
	},
	organization: {
		all: (orgId: string) => ["organization", orgId] as const,
		userOrganizations: () => ["organization", "userOrganizations"] as const,
		details: (orgId: string) => ["organization", orgId, "details"] as const,
		users: (orgId: string) => ["organization", orgId, "users"] as const,
		invitations: (orgId: string) =>
			["organization", orgId, "invitations"] as const,
		templates: (orgId: string) => ["organization", orgId, "templates"] as const,
	},
};
